import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  FormHelperText,
  Divider,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  CustomTrainingForm,
  CreateFormRequest,
  CustomTrainingField,
  FormFieldInput,
  FieldType,
} from '../../models/training.models';
import useService from '../../hooks/service.hook';
import CustomTrainingFieldsService from '../../services/custom-training-fields.service';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import { palette } from '../../theme';

export interface CustomTrainingFormDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (data: CreateFormRequest) => void;
  isLoading: boolean;
  existingForm?: CustomTrainingForm | null;
  teamId: string;
}

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Text',
  number: 'Number',
  enum: 'Dropdown',
  rating: 'Rating',
  full_team_number: 'Per-Athlete',
  target_recording: 'Target Recording',
};

const FIELD_TYPE_COLORS: Record<
  FieldType,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  text: 'default',
  number: 'info',
  enum: 'secondary',
  rating: 'warning',
  full_team_number: 'success',
  target_recording: 'primary',
};

export default function CustomTrainingFormDialog(props: CustomTrainingFormDialogProps) {
  const isEditMode = !!props.existingForm;
  const customFieldsService = useService(CustomTrainingFieldsService);
  const { showSnackbar } = useSnackbarHelper();

  const [formData, setFormData] = useState<{ name: string; description: string }>({
    name: '',
    description: '',
  });

  const [availableFields, setAvailableFields] = useState<CustomTrainingField[]>([]);
  const [selectedFields, setSelectedFields] = useState<FormFieldInput[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState<boolean>(false);

  const [nameError, setNameError] = useState<string>('');
  const [fieldsError, setFieldsError] = useState<string>('');

  useEffect(() => {
    if (props.isOpen) {
      fetchAvailableFields();

      if (props.existingForm) {
        setFormData({
          name: props.existingForm.name,
          description: props.existingForm.description || '',
        });
        setSelectedFields(
          props.existingForm.fields.map((f) => ({
            field_id: f.field_id,
            order: f.order,
          }))
        );
      } else {
        setFormData({ name: '', description: '' });
        setSelectedFields([]);
      }
      setNameError('');
      setFieldsError('');
    }
  }, [props.isOpen, props.existingForm]);

  const fetchAvailableFields = () => {
    setFieldsLoading(true);
    customFieldsService
      .listFields(props.teamId)
      .then((response) => {
        setAvailableFields(response.data.data || []);
        setFieldsLoading(false);
      })
      .catch(() => {
        showSnackbar('Error loading custom fields', 'error');
        setFieldsLoading(false);
      });
  };

  const closeModal = () => {
    props.handleClose();
  };

  const handleAddField = (fieldId: number) => {
    if (selectedFields.some((f) => f.field_id === fieldId)) {
      return; // Already added
    }
    const newField: FormFieldInput = {
      field_id: fieldId,
      order: selectedFields.length,
    };
    setSelectedFields([...selectedFields, newField]);
  };

  const handleRemoveField = (fieldId: number) => {
    const filtered = selectedFields.filter((f) => f.field_id !== fieldId);
    // Re-index the order
    const reindexed = filtered.map((f, index) => ({ ...f, order: index }));
    setSelectedFields(reindexed);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(selectedFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const reindexed = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    setSelectedFields(reindexed);
  };

  const getFieldById = (fieldId: number): CustomTrainingField | undefined => {
    return availableFields.find((f) => f.id === fieldId);
  };

  const validateAndSubmit = () => {
    setNameError('');
    setFieldsError('');

    if (!formData.name.trim()) {
      setNameError('Form name is required');
      return;
    }

    if (formData.name.length > 250) {
      setNameError('Form name cannot exceed 250 characters');
      return;
    }

    if (selectedFields.length === 0) {
      setFieldsError('At least one field is required');
      return;
    }

    const submitData: CreateFormRequest = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      fields: selectedFields,
    };

    props.handleSubmit(submitData);
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      {/* Title row */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'text.primary' }}>
          {isEditMode ? 'Edit Training Form' : 'New Training Form'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          size="small"
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Form Name */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
            Form Name
          </Typography>
          <TextField
            required
            name="formName"
            value={formData.name}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="e.g. Race Day Assessment"
            onChange={(evt) => setFormData({ ...formData, name: evt.target.value })}
            error={!!nameError}
            helperText={nameError || `${formData.name.length}/250 characters`}
            inputProps={{ maxLength: 250 }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
            Description
          </Typography>
          <TextField
            name="description"
            value={formData.description}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="What is this form for?"
            onChange={(evt) => setFormData({ ...formData, description: evt.target.value })}
          />
        </Box>

        {/* Fields section */}
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Fields
        </Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, display: 'flex', minHeight: 300, overflow: 'hidden' }}>
          {/* Available column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                Available
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 1, pb: 1 }}>
              {fieldsLoading ? (
                <Typography color="textSecondary" sx={{ p: 2, fontSize: '0.875rem' }}>
                  Loading fields...
                </Typography>
              ) : availableFields.length === 0 ? (
                <Typography color="textSecondary" sx={{ p: 2, fontSize: '0.875rem' }}>
                  No custom fields available. Create some fields first.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {availableFields.map((field) => {
                    const isSelected = selectedFields.some((f) => f.field_id === field.id);
                    return (
                      <ListItem
                        key={field.id}
                        sx={{
                          borderRadius: 1,
                          '&:hover': { bgcolor: palette.grey[50] },
                          opacity: isSelected ? 0.45 : 1,
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="add"
                            onClick={() => handleAddField(field.id)}
                            disabled={isSelected}
                            size="small"
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: palette.primary.main,
                              color: '#fff',
                              borderRadius: '50%',
                              '&:hover': { bgcolor: palette.primary.dark },
                              '&.Mui-disabled': { bgcolor: palette.grey[200], color: palette.grey[400] },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {field.name}
                            </Typography>
                          }
                          secondary={
                            <Chip
                              label={FIELD_TYPE_LABELS[field.field_type]}
                              size="small"
                              color={FIELD_TYPE_COLORS[field.field_type]}
                              sx={{ mt: 0.5 }}
                            />
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Selected column */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: palette.background.subtle,
            }}
          >
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                Selected ({selectedFields.length})
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 1, pb: 1 }}>
              {selectedFields.length === 0 ? (
                <Typography color="textSecondary" sx={{ p: 2, textAlign: 'center', fontSize: '0.875rem' }}>
                  Add fields from the left
                </Typography>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="selected-fields">
                    {(provided) => (
                      <List {...provided.droppableProps} ref={provided.innerRef} dense disablePadding>
                        {selectedFields.map((fieldInput, index) => {
                          const field = getFieldById(fieldInput.field_id);
                          if (!field) return null;

                          return (
                            <Draggable key={field.id} draggableId={field.id.toString()} index={index}>
                              {(provided, snapshot) => (
                                <Paper
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  elevation={snapshot.isDragging ? 3 : 0}
                                  sx={{
                                    mb: 0.5,
                                    borderRadius: 1.5,
                                    bgcolor: snapshot.isDragging ? palette.primary.light : palette.background.paper,
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <ListItem
                                    sx={{
                                      borderRadius: 1.5,
                                      '&:hover': { bgcolor: palette.grey[50] },
                                    }}
                                    secondaryAction={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton
                                          aria-label="remove"
                                          onClick={() => handleRemoveField(field.id)}
                                          size="small"
                                          sx={{
                                            width: 26,
                                            height: 26,
                                            bgcolor: palette.error.light,
                                            color: palette.error.main,
                                            borderRadius: '50%',
                                            '&:hover': {
                                              bgcolor: palette.error.main,
                                              color: '#fff',
                                            },
                                          }}
                                        >
                                          <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        <IconButton
                                          {...provided.dragHandleProps}
                                          size="small"
                                          sx={{
                                            cursor: 'grab',
                                            color: palette.grey[300],
                                            '&:hover': { color: palette.grey[500] },
                                          }}
                                        >
                                          <DragIndicatorIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    }
                                  >
                                    <Box sx={{ mr: 1.5, minWidth: 20 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        {index + 1}
                                      </Typography>
                                    </Box>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {field.name}
                                        </Typography>
                                      }
                                      secondary={
                                        <Chip
                                          label={FIELD_TYPE_LABELS[field.field_type]}
                                          size="small"
                                          color={FIELD_TYPE_COLORS[field.field_type]}
                                          sx={{ mt: 0.5 }}
                                        />
                                      }
                                    />
                                  </ListItem>
                                </Paper>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Box>
          </Box>
        </Paper>
        {fieldsError && (
          <FormHelperText error sx={{ mt: 1 }}>
            {fieldsError}
          </FormHelperText>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={closeModal}
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={validateAndSubmit}
          disabled={props.isLoading}
          variant="contained"
          sx={{ borderRadius: 50, px: 3 }}
        >
          {isEditMode ? 'Update Form' : 'Create Form'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
